import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { Cartao } from '../../componentes/base';
import { CampoControlado } from '../../componentes/formulario';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { get } from '../../nucleo/api';
import { data, dinheiro, documentoFormatado, nomePessoa } from '../../nucleo/formato';
import { DetalheFornecedor } from './DetalheFornecedor';
import { TabelaPrecos } from './TabelaPrecos';
import type { Fornecedor, ResumoPreco } from './tipos';
import { AvisoModo } from './AvisoModo';
import { TextoCortado } from '../../componentes/texto-cortado';

/** Consulta de preços de um produto entre os fornecedores (M10.4): último, média, menor e maior, com o mais barato marcado. */
function ConsultaPrecos() {
  const { t } = useTranslation('compras');
  const [produtoId, setProdutoId] = useState<string | null>(null);
  const q = useQuery({ queryKey: ['/compras/precos', 'resumo', produtoId], queryFn: () => get<ResumoPreco[]>('/compras/precos/resumo', { produto_id: produtoId }), enabled: !!produtoId });
  return (
    <Cartao titulo={t('precos.titulo')}>
      <div className="flex min-w-0 flex-col gap-3">
        <p className="text-sm text-tinta-3">{t('precos.explicacao')}</p>
        <div className="max-w-md">
          <CampoControlado
            def={{ nome: 'produto_consulta', rotulo: t('campos.produto'), tipo: 'busca', busca: { caminho: '/estoque/produtos', rotulo: (i) => `${String(i.codigo)} ${String(i.nome)}` } }}
            valor={produtoId}
            aoMudar={(v) => setProdutoId((v as string | null) || null)}
          />
        </div>
        {produtoId && q.isPending && <Carregando />}
        {produtoId && q.isError && <Erro erro={q.error} aoTentar={() => q.refetch()} />}
        {produtoId && q.data && <TabelaPrecos linhas={q.data} />}
      </div>
    </Cartao>
  );
}

/** Aba Fornecedores (M10.2): condição de pagamento e prazo padrão de cada fornecedor, e o histórico de preços. */
export function Fornecedores() {
  const { t } = useTranslation('compras');
  const [aberto, setAberto] = useState<string | null>(null);

  const colunas: Coluna<Fornecedor>[] = [
    {
      chave: 'nome',
      rotulo: t('campos.fornecedor'),
      principal: true,
      ordenavel: true,
      render: (f) => (
        <span className="flex min-w-0 max-w-sm flex-col">
          <span className="truncate font-medium" title={nomePessoa(f.nome)}>{nomePessoa(f.nome)}</span>
          {f.documento && <TextoCortado className="numero text-xs text-tinta-3">{documentoFormatado(f.documento)}</TextoCortado>}
        </span>
      ),
    },
    { chave: 'condicao', rotulo: t('campos.condicao'), mono: true, render: (f) => (f.condicao_pagamento === null ? <span className="text-tinta-3">{t('fornecedor.naoDefinida')}</span> : f.condicao_pagamento === 'avista' ? t('pedido.avista') : f.condicao_pagamento) },
    { chave: 'prazo', rotulo: t('campos.prazoEntregaDias'), alinhar: 'direita', mono: true, render: (f) => (f.prazo_entrega_dias === null ? '' : t('comparativo.prazoDias', { count: f.prazo_entrega_dias })) },
    { chave: 'pedidos', rotulo: t('campos.pedidos'), alinhar: 'direita', mono: true, render: (f) => f.pedidos },
    { chave: 'a_receber', rotulo: t('campos.aReceber'), alinhar: 'direita', mono: true, render: (f) => f.a_receber },
    { chave: 'comprado', rotulo: t('campos.comprado'), ordenavel: true, alinhar: 'direita', mono: true, render: (f) => dinheiro(f.comprado_centavos) },
    { chave: 'ultimo_pedido', rotulo: t('campos.ultimoPedido'), oculta: true, mono: true, render: (f) => (f.ultimo_pedido ? data(f.ultimo_pedido) : '') },
  ];

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <AvisoModo />
      <ConsultaPrecos />
      <Tabela<Fornecedor> tela="compras.fornecedores" caminho="/compras/fornecedores" colunas={colunas} aoClicar={(f) => setAberto(f.id)} vazio={<Vazio texto={t('vazio.fornecedores')} />} />
      <DetalheFornecedor id={aberto} aoFechar={() => setAberto(null)} />
    </div>
  );
}
